/**
 * Fulfilments — `/sales/fulfilments`.
 *
 * What the warehouse is doing about the branch's orders. It is a separate list
 * from orders because it is a separate record: an imported order is a mirror
 * of the channel's state, and picking against it must not rewrite what the
 * channel said.
 *
 * The one thing this screen insists on is that shipping is not undoable here.
 * Nothing follows `shipped` — after the goods are with the carrier the
 * correction is a return, not a cancellation — so the row offers no action
 * that would pretend otherwise.
 */
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { ColumnDef } from "@tanstack/react-table";
import {
  Badge,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@erp/ui";

import { DataTable, dateTimeCell, textCell } from "@/components/data-table";

import {
  FULFILMENT_STATUSES,
  type FulfilmentFilters,
  type FulfilmentSummary,
} from "../fulfilments-api";
import { useFulfilmentList } from "../queries";

const ALL = "__all__";

export interface FulfilmentsPageProps {
  onOpenFulfilment: (fulfilmentId: string) => void;
  pageLimit?: number;
}

export function FulfilmentsPage({ onOpenFulfilment, pageLimit }: FulfilmentsPageProps) {
  const { t } = useTranslation();
  const [status, setStatus] = useState<string>(ALL);

  const filters = useMemo<FulfilmentFilters>(() => {
    const built: FulfilmentFilters = {};
    if (status !== ALL) built.status = status as FulfilmentFilters["status"];
    return built;
  }, [status]);

  const query = useFulfilmentList(filters, pageLimit);

  const columns = useMemo<ColumnDef<FulfilmentSummary, unknown>[]>(
    () => [
      {
        id: "fulfilment_number",
        accessorKey: "fulfilment_number",
        header: t("sales.fulfilments.columns.number", "Shipment"),
        cell: ({ row }) => (
          <span dir="ltr" className="font-mono text-xs">
            {row.original.fulfilment_number}
          </span>
        ),
      },
      {
        id: "status",
        accessorKey: "status",
        header: t("sales.fulfilments.columns.status", "Status"),
        cell: ({ row }) => (
          <Badge variant={row.original.status === "shipped" ? "success" : "secondary"}>
            {t(`sales.fulfilmentStatus.${row.original.status}`, row.original.status)}
          </Badge>
        ),
      },
      {
        id: "carrier",
        accessorKey: "carrier",
        header: t("sales.fulfilments.columns.carrier", "Carrier"),
        cell: ({ row }) =>
          textCell(
            row.original.carrier === ""
              ? "—"
              : `${row.original.carrier} ${row.original.tracking_reference}`.trim(),
          ),
      },
      {
        id: "shipped_at",
        accessorKey: "shipped_at",
        header: t("sales.fulfilments.columns.shipped", "Shipped"),
        cell: ({ row }) =>
          row.original.shipped_at === null ? textCell("—") : dateTimeCell(row.original.shipped_at),
      },
    ],
    [t],
  );

  return (
    <div className="flex flex-col gap-4 p-4">
      <div className="space-y-1">
        <h1 className="text-xl font-semibold tracking-tight">
          {t("sales.fulfilments.title", "Shipments")}
        </h1>
        <p className="text-sm text-muted-foreground">
          {t(
            "sales.fulfilments.subtitle",
            "What the warehouse is doing about the branch's orders. Shipping is the moment goods leave — and the moment the ledger hears about the cost.",
          )}
        </p>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1.5">
          <Label htmlFor="fulfilment-status">
            {t("sales.fulfilments.columns.status", "Status")}
          </Label>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger id="fulfilment-status" className="w-48">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>{t("filters.all", "All")}</SelectItem>
              {FULFILMENT_STATUSES.map((value) => (
                <SelectItem key={value} value={value}>
                  {t(`sales.fulfilmentStatus.${value}`, value)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <DataTable
        columns={columns}
        query={query}
        getRowId={(row) => row.id}
        onRowClick={(row) => onOpenFulfilment(row.id)}
        emptyMessage={t("sales.fulfilments.empty", "No shipment matches these filters.")}
      />
    </div>
  );
}
